import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Alert, Button } from '../ui';
import { 
  ArrowPathIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import processService from '../../services/processService';

// Import existing monitor components
import BasicInfoProcessMonitor from '../BasicInfoProcessMonitor';
import StandardAIAnalysisProcessMonitor from '../StandardAIAnalysisProcessMonitor';
import AccountScannerProcessMonitor from '../AccountScannerProcessMonitor';
import GroupJoinProcessMonitor from '../GroupJoinProcessMonitor';
import UnsubscribeProcessMonitor from '../UnsubscribeProcessMonitor';

const ProcessRestorer = () => {
  const [restoredProcesses, setRestoredProcesses] = useState([]);
  const [error, setError] = useState(null);
  const [showRestoreAlert, setShowRestoreAlert] = useState(false);
  const [manualRestore, setManualRestore] = useState(false);

  // Query active processes on mount
  const { 
    data: activeProcesses = [], 
    isLoading, 
    error: queryError,
    refetch
  } = useQuery(
    ['processRestorer', 'activeProcesses'],
    () => processService.getActiveProcesses().then(res => res.processes || []),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 0, // Always fetch fresh data
      cacheTime: 0, // Don't cache
      onError: (err) => {
        console.error('Error fetching active processes for restoration:', err);
        setError(`Failed to restore processes: ${err.message}`);
      }
    }
  );

  // Auto-restore processes on component mount
  useEffect(() => {
    if (activeProcesses.length > 0 && !manualRestore) {
      console.log(`🔄 Process Restorer: Found ${activeProcesses.length} active processes to restore`);
      
      const processesToRestore = activeProcesses.filter(process => {
        // Only restore processes that are actually running
        return ['pending', 'in_progress'].includes(process.status);
      });

      if (processesToRestore.length > 0) {
        setRestoredProcesses(processesToRestore);
        setShowRestoreAlert(true);
        
        // Auto-hide alert after 10 seconds
        setTimeout(() => {
          setShowRestoreAlert(false);
        }, 10000);
      }
    }
  }, [activeProcesses, manualRestore]);

  // Manual restore function
  const handleManualRestore = async () => {
    try {
      setManualRestore(true);
      setError(null);
      
      const result = await refetch();
      const processes = result.data || [];
      
      const processesToRestore = processes.filter(process => {
        return ['pending', 'in_progress'].includes(process.status);
      });

      if (processesToRestore.length > 0) {
        setRestoredProcesses(processesToRestore);
        setShowRestoreAlert(true);
        
        // Auto-hide alert after 10 seconds
        setTimeout(() => {
          setShowRestoreAlert(false);
        }, 10000);
      } else {
        setError('No active processes found to restore');
      }
    } catch (err) {
      console.error('Error during manual restore:', err);
      setError(`Manual restore failed: ${err.message}`);
    } finally {
      setManualRestore(false);
    }
  };

  // Remove process from restored list
  const handleProcessClose = (processId) => {
    setRestoredProcesses(prev => prev.filter(p => p.id !== processId));
  };

  // Render process monitor based on type
  const renderProcessMonitor = (process) => {
    const commonProps = {
      key: process.id,
      processId: process.id,
      onClose: () => handleProcessClose(process.id)
    };

    switch (process.process_type) {
      case 'BASIC_INFO':
        return <BasicInfoProcessMonitor {...commonProps} />;
      case 'GROUP_JOIN':
      case 'JOIN_GROUPS':
        return <GroupJoinProcessMonitor {...commonProps} />;
      case 'AI_ANALYSIS':
        return <StandardAIAnalysisProcessMonitor {...commonProps} />;
      case 'ACCOUNT_SCAN':
        return <AccountScannerProcessMonitor {...commonProps} />;
      case 'UNSUBSCRIBE':
        return <UnsubscribeProcessMonitor {...commonProps} />;
      default:
        console.warn(`Unknown process type: ${process.process_type}`);
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Manual Restore Button */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <PlayIcon className="h-5 w-5 text-blue-500" />
          <span className="text-sm font-medium text-gray-700">Process Recovery</span>
        </div>
        <Button
          variant="secondary"
          size="sm"
          icon={ArrowPathIcon}
          onClick={handleManualRestore}
          disabled={isLoading || manualRestore}
        >
          {isLoading || manualRestore ? 'Restoring...' : 'Restore Processes'}
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert 
          variant="error" 
          className="mb-4"
          onClose={() => setError(null)}
        >
          <ExclamationTriangleIcon className="h-5 w-5" />
          <span className="ml-2">{error}</span>
        </Alert>
      )}

      {/* Restoration Success Alert */}
      {showRestoreAlert && restoredProcesses.length > 0 && (
        <Alert 
          variant="success" 
          className="mb-4"
          onClose={() => setShowRestoreAlert(false)}
        >
          <CheckCircleIcon className="h-5 w-5" />
          <div className="ml-2">
            <div className="font-medium">
              ✅ Restored {restoredProcesses.length} active process{restoredProcesses.length !== 1 ? 'es' : ''}
            </div>
            <div className="text-sm mt-1">
              {restoredProcesses.map(p => (
                <div key={p.id} className="flex items-center space-x-2">
                  <span>{processService.getProcessTypeIcon(p.process_type)}</span>
                  <span>{processService.getProcessTypeDisplayName(p.process_type)}</span>
                  <span className="text-xs text-gray-600">
                    ({p.progress.processed}/{p.progress.total} - {processService.calculateProgressPercentage(p.progress)}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Alert>
      )}

      {/* No Active Processes Info */}
      {!isLoading && activeProcesses.length === 0 && !error && (
        <Alert variant="info" className="mb-4">
          <CheckCircleIcon className="h-5 w-5" />
          <span className="ml-2">No active processes to restore</span>
        </Alert>
      )}

      {/* Rendered Process Monitors */}
      {restoredProcesses.map(process => renderProcessMonitor(process))}
    </div>
  );
};

export default ProcessRestorer;
